import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Experimental_CssVarsProvider as CssVarsProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box } from '@mui/material';

// Auth Components
import { AuthProvider } from './auth/auth.context';
import RequireAuth from './auth/RequireAuth';

// Components
import Sidebar from './components/Sidebar';
import TopBar from './components/TopBar';

// Pages
import UserManagement from './pages/UserManagement';
import CompanyManagement from './pages/CompanyManagement';
import FileManagement from './pages/FileManagement';
import CourseManagement from './pages/CourseManagement';
import ModuleManagement from './pages/ModuleManagement';
import ResourcesManagement from './pages/ResourcesManagement';
import Dashboard from './pages/Dashboard';
import Login from './pages/Login';
import Unauthorized from './pages/Unauthorized';
import StepEditor from './pages/StepEditor';
import VideoStepEditor from './pages/VideoStepEditor';
import ImageStepEditor from './pages/ImageStepEditor';
// Theme
import AppTheme from './theme/AppTheme';
import EditVideoStep from './pages/EditVideoStep';
import EditImageStep from './pages/EditImageStep';
import EditStepEditor from './pages/EditStepEditor';

const DRAWER_WIDTH = 260;
const COLLAPSED_DRAWER_WIDTH = 80;

// Protected Layout Component
function ProtectedLayout({ children }) {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [isCollapsed, setIsCollapsed] = React.useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleToggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const currentDrawerWidth = isCollapsed ? COLLAPSED_DRAWER_WIDTH : DRAWER_WIDTH;

  return (
    <Box sx={{ display: 'flex' }}>
      <TopBar
        drawerWidth={currentDrawerWidth}
        onDrawerToggle={handleDrawerToggle}
        onToggleCollapse={handleToggleCollapse}
        isCollapsed={isCollapsed}
      />
      <Sidebar
        drawerWidth={DRAWER_WIDTH}
        collapsedWidth={COLLAPSED_DRAWER_WIDTH}
        mobileOpen={mobileOpen}
        isCollapsed={isCollapsed}
        onDrawerToggle={handleDrawerToggle}
      />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1, sm: 3 },
          width: { sm: `calc(100% - ${currentDrawerWidth}px)` },
          mt: 8, // Account for top bar height
          transition: (theme) => theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

// Full Screen Layout Component (No Sidebar, No TopBar)
function FullScreenLayout({ children }) {
  return (
    <Box sx={{ display: 'flex', height: '100vh', width: '100vw', overflow: 'hidden' }}>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          height: '100%',
          width: '100%',
          overflow: 'auto',
          p: 0, // No padding for full screen
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

function App() {
  return (
    <CssVarsProvider defaultMode="system">
      <AppTheme>
        <CssBaseline enableColorScheme />
        <AuthProvider>
          <Router>
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/unauthorized" element={<Unauthorized />} />

              {/* Protected Routes */}
              <Route path="/" element={
                <RequireAuth>
                  <ProtectedLayout>
                    <Dashboard />
                  </ProtectedLayout>
                </RequireAuth>
              } />
              <Route path="/dashboard" element={
                <RequireAuth>
                  <ProtectedLayout>
                    <Dashboard />
                  </ProtectedLayout>
                </RequireAuth>
              } />
              <Route path="/users" element={
                <RequireAuth>
                  <ProtectedLayout>
                    <UserManagement />
                  </ProtectedLayout>
                </RequireAuth>
              } />
              <Route path="/companies" element={
                <RequireAuth>
                  <ProtectedLayout>
                    <CompanyManagement />
                  </ProtectedLayout>
                </RequireAuth>
              } />
              <Route path="/files" element={
                <RequireAuth>
                  <ProtectedLayout>
                    <FileManagement />
                  </ProtectedLayout>
                </RequireAuth>
              } />
              <Route path="/courses" element={
                <RequireAuth>
                  <ProtectedLayout>
                    <CourseManagement />
                  </ProtectedLayout>
                </RequireAuth>
              } />
              <Route path="/modules" element={
                <RequireAuth>
                  <ProtectedLayout>
                    <ModuleManagement />
                  </ProtectedLayout>
                </RequireAuth>
              } />
              <Route path="/resources" element={
                <RequireAuth>
                  <ProtectedLayout>
                    <ResourcesManagement />
                  </ProtectedLayout>
                </RequireAuth>
              } />
              <Route path="/module/step-editor/:id" element={
                <RequireAuth>
                  <FullScreenLayout>
                    <StepEditor />
                  </FullScreenLayout>
                </RequireAuth>
              } />
              <Route path="/module/video-step-editor/:id" element={
                <RequireAuth>
                  <FullScreenLayout>
                    <VideoStepEditor />
                  </FullScreenLayout>
                </RequireAuth>
              } />
              <Route path="/module/image-step-editor/:id" element={
                <RequireAuth>
                  <FullScreenLayout>
                    <ImageStepEditor />
                  </FullScreenLayout>
                </RequireAuth>
              } />
              <Route path="/module/:moduleId/edit-step-editor/:stepId" element={
                <RequireAuth>
                  <FullScreenLayout>
                    <EditStepEditor />
                  </FullScreenLayout>
                </RequireAuth>
              } />
              <Route path="/module/:moduleId/edit-video-step/:stepId" element={
                <RequireAuth>
                  <FullScreenLayout>
                    <EditVideoStep />
                  </FullScreenLayout>
                </RequireAuth>
              } />
              <Route path="/module/:moduleId/edit-image-step/:stepId" element={
                <RequireAuth>
                  <FullScreenLayout>
                    <EditImageStep />
                  </FullScreenLayout>
                </RequireAuth>
              } />
            </Routes>
          </Router>
        </AuthProvider>
      </AppTheme>
    </CssVarsProvider>
  );
}

export default App;
